import { useContext, useMemo } from 'react';
import { AppFormContext } from 'contexts/AppFormContext';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useDisplayFetchDataError } from 'hooks/useDisplayFetchDataError/useDisplayFetchDataError';
import { SimpleSelectElement } from 'components/molecules';
import { commonTexts, timetableTexts } from 'consts/text';
import { parseJournalDTOIntoSelectOptionType } from 'utils/parsers/journal/parseJournalDTOIntoSelectOptionType';
import { formFields } from 'consts/form/formFields';
import { EmployeeId, JournalDTO, JournalId } from 'types';
import { journalFields } from 'consts/journal/journalFields';

type JournalPickerProps = {
    onChangeJournal?: (journalData: JournalDTO) => void;
    onClear?: () => void;
    disabled?: boolean;
    allowClear?: boolean;
    teacherIds?: EmployeeId[];
};

export const JournalPicker = ({
    onChangeJournal,
    onClear,
    disabled = false,
    allowClear,
    teacherIds,
}: JournalPickerProps) => {
    const { formError, setFormError } = useContext(AppFormContext);

    const {
        data: journalsData,
        isFetching: isJournalsDataFetching,
        isError: isJournalsDataError,
    } = useAppQuery(
        'LESSONS_JOURNALS',
        [teacherIds, 'SPECIAL'],
        () =>
            StaffHTTPService.employeeSchedule.getCommonJournals({
                teacherIds: teacherIds || [],
                type: 'SPECIAL',
            }),
        {
            enabled: !!teacherIds?.length,
        },
    );

    useDisplayFetchDataError({
        isFetching: isJournalsDataFetching,
        isError: isJournalsDataError,
        actualError: formError,
        errorMessage: commonTexts.errorMessages.journalsFetching,
        setError: setFormError,
    });

    const handleChangeJournal = (journalId: JournalId) => {
        if (!journalsData) {
            return;
        }
        const selectedJournal = journalsData.find(
            (journalsDataItem) => journalsDataItem[journalFields.id] === journalId,
        );
        if (!selectedJournal) {
            return;
        }
        onChangeJournal?.(selectedJournal);
    };

    const journalsOptions = useMemo(() => {
        if (!journalsData) {
            return [];
        }
        return journalsData.map(parseJournalDTOIntoSelectOptionType);
    }, [journalsData]);

    return (
        <SimpleSelectElement
            name={formFields.journalId}
            label={timetableTexts.lessons.linkJournal}
            options={journalsOptions}
            loading={isJournalsDataFetching}
            onChange={handleChangeJournal}
            onClear={onClear}
            allowClear={allowClear}
            disabled={disabled}
            placeholder="Wybierz dziennik"
        />
    );
};
